/* components/ui/Badge.jsx — бейджи и пилюли. ВСЕГДА цвет + глиф/текст (доступность).
   Pill           — базовая цветная пилюля (tone via пропс).
   StatusPill     — Pill с предустановленным tone по строковому status.
   KindBadge      — вид сущности (Блюдо/Полуфабрикат/Ингредиент); иконка+текст.
   StatusBadge    — статус ТТК/меню (ready/unbound/stub/draft/done/failed); цвет+глиф.
   CostStatusBadge— полнота себестоимости (ok/partial/missing).
   FoodCostBadge  — FC% с цветовой шкалой (<30 зел / 30–40 жёлт / >40 красн).
   RecipeBindingBadge — привязка строки ТТК; read-only НЕ выглядит кнопкой (баг §15.3).
   ХЕНДОФФ → frontend/components/common/. */

import { Icon } from './Icon.jsx';
import { fmt } from '../../lib/format.js';
import { enums as E } from '../../lib/enums.js';

  // Единый словарь тон→глиф (badge-токены). Toast потребляет его же после маппинга
  // семантический→badge-токен (enums.TONE_TOKEN), чтобы Badge и Toast не дрейфовали (P2-04).
  const TONE_GLYPH = { ok: 'check', warn: 'alert-triangle', bad: 'alert-circle', info: 'info', accent: 'sparkles', neutral: null };
  const TONE_CLASS = { ok: 'ok', warn: 'warn', bad: 'bad', info: 'info', accent: 'accent', neutral: '' };

  /* ── Pill ── */
  function Pill({ children, tone = 'neutral', glyph, icon, size = 'md', className }) {
    const g = icon || (glyph === false ? null : (glyph || TONE_GLYPH[tone]));
    const cls = ['badge', TONE_CLASS[tone] ? 'badge--' + TONE_CLASS[tone] : '', size === 'sm' ? 'badge--sm' : '', className || ''].filter(Boolean).join(' ');
    return (
      <span className={cls}>
        {g && <Icon name={g} size={12} />}
        <span>{children}</span>
      </span>
    );
  }

  /* ── StatusPill / StatusBadge (один маппинг status→tone+label) ── */
  function StatusBadge({ status, label, size = 'md', className }) {
    const tone = E.STATUS_TONE[status] || 'neutral';
    const text = label || E.STATUS_LABEL[status] || status;
    return <Pill tone={tone} size={size} className={className}>{text}</Pill>;
  }
  const StatusPill = StatusBadge; // алиас: то же поведение

  /* ── CostStatusBadge ── */
  function CostStatusBadge({ status, size = 'md', className }) {
    return <StatusBadge status={status} size={size} className={className} />;
  }

  /* ── KindBadge ──
     Метит ВИД сущности (Блюдо/Полуфабрикат/Ингредиент/Меню). «ТТК» — только сама
     технологическая карта, не метка блюда: kind 'ttk'/'card' = синонимы 'dish' → «Блюдо»
     (глоссарий 04 §14; P3-12). Отдельной метки «ТТК» в реестре видов нет. */
  const KIND = {
    dish:  { label: 'Блюдо', icon: 'utensils', tone: 'accent' },
    semi:  { label: 'Полуфабрикат', icon: 'box', tone: 'info' },
    ingredient: { label: 'Ингредиент', icon: 'tag', tone: 'neutral' },
    card:  { label: 'Блюдо', icon: 'utensils', tone: 'accent' },
    ttk:   { label: 'Блюдо', icon: 'utensils', tone: 'accent' },
    menu:  { label: 'Меню', icon: 'layers', tone: 'info' },
  };
  function KindBadge({ kind = 'dish', label, size = 'md', className }) {
    const k = KIND[kind] || KIND.dish;
    return (
      <span className={'badge badge--' + (k.tone === 'neutral' ? '' : k.tone) + ' kind-badge' + (size === 'sm' ? ' badge--sm' : '') + (className ? ' ' + className : '')}>
        <Icon name={k.icon} size={12} />
        <span>{label || k.label}</span>
      </span>
    );
  }

  /* ── FoodCostBadge: цветовая шкала FC% ── */
  function fcTone(v) { return v == null ? 'neutral' : v < 30 ? 'ok' : v <= 40 ? 'warn' : 'bad'; }
  function FoodCostBadge({ value, size = 'md', showLabel = true, className }) {
    const tone = fcTone(value);
    return (
      <span className={'badge fc-badge badge--' + (tone === 'neutral' ? '' : tone) + (size === 'sm' ? ' badge--sm' : '') + (className ? ' ' + className : '')}>
        {showLabel && <span className="fc-badge__key">FC</span>}
        <span className="num">{value == null ? '—' : fmt.percent(value)}</span>
      </span>
    );
  }

  /* ── RecipeBindingBadge ── (read-only ≠ кнопка; баг §15.3 п.23) */
  function RecipeBindingBadge({ state = 'bound', onAdd, readOnly = false, className }) {
    if (state === 'bound') {
      return <span className={'binding binding--bound' + (className ? ' ' + className : '')}><Icon name="link" size={13} />Привязан</span>;
    }
    if (state === 'unbound') {
      return <span className={'binding binding--unbound' + (className ? ' ' + className : '')}><Icon name="unlink" size={13} />Не привязан</span>;
    }
    // state === 'add' — кликабельно ТОЛЬКО если не readOnly
    if (readOnly) {
      return <span className={'binding binding--unbound' + (className ? ' ' + className : '')}><Icon name="unlink" size={13} />Не привязан</span>;
    }
    return (
      <button type="button" className={'binding binding--add' + (className ? ' ' + className : '')} onClick={onAdd}>
        <Icon name="plus" size={13} />Привязать
      </button>
    );
  }

export { Pill };
export { StatusPill };
export { StatusBadge };
export { CostStatusBadge };
export { KindBadge };
export { FoodCostBadge };
export { RecipeBindingBadge };
export { fcTone };
export { TONE_GLYPH };
